import React from 'react';
import { User } from '@/services/userService';
import { useAuth } from '@/hooks/useAuth';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { Icon } from '@/components/ui/icons';

interface UserDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
}

export const UserDetailModal: React.FC<UserDetailModalProps> = ({ isOpen, onClose, user }) => {
  const { isFieldAllowed } = useAuth();
  const canName = isFieldAllowed('name');
  const canUserName = isFieldAllowed('userName');
  const canCorreo = isFieldAllowed('correo');
  const canOwnerID = isFieldAllowed('ownerID');
  const canPuesto = isFieldAllowed('puesto');
  const canRegion = isFieldAllowed('region');
  const canUserEstatus = isFieldAllowed('userEstatus');
  const canTipoUsuario = isFieldAllowed('tipoUsuario');
  const canAcl = isFieldAllowed('acl');

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Detalle del Usuario">
      <div className="space-y-6">
        {(canName || canUserName) && (
        <div className="flex items-center space-x-4 pb-4 border-b border-gray-200">
          <div className="flex-shrink-0 h-16 w-16 bg-gradient-to-br from-[#0196A1] to-[#017a85] rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
            {(canName ? user.name : user.userName || '?').charAt(0).toUpperCase()}
          </div>
          <div>
            {canName && <h3 className="text-xl font-bold text-gray-900">{user.name}</h3>}
            {canUserName && <p className="text-sm text-gray-500">@{user.userName}</p>}
          </div>
        </div>
        )}

        {(canName || canUserName || canCorreo || canOwnerID) && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Icon name="user" size={16} />
            Información Personal
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {canName && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Nombre Completo</label>
              <p className="text-sm text-gray-900">{user.name}</p>
            </div>
            )}
            {canUserName && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Nombre de Usuario</label>
              <p className="text-sm text-gray-900">@{user.userName}</p>
            </div>
            )}
            {canCorreo && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Correo Electrónico</label>
              <p className="text-sm text-gray-900">{user.correo}</p>
            </div>
            )}
            {canOwnerID && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">ID de Usuario</label>
              <p className="text-sm text-gray-900 font-mono">{user.ownerID}</p>
            </div>
            )}
          </div>
        </div>
        )}

        {(canAcl || canPuesto || canRegion || canTipoUsuario) && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Icon name="building" size={16} />
            Información Laboral
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {canAcl && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Rol (ACL)</label>
              <div className="mt-1">
                <Badge variant="info">{user.acl}</Badge>
              </div>
            </div>
            )}
            {canPuesto && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Puesto</label>
              <p className="text-sm text-gray-900">{user.puesto || 'No especificado'}</p>
            </div>
            )}
            {canRegion && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Región</label>
              <p className="text-sm text-gray-900">{user.region || 'No especificada'}</p>
            </div>
            )}
            {canTipoUsuario && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Tipo de Usuario</label>
              <p className="text-sm text-gray-900">{user.tipoUsuario || 'Interno'}</p>
            </div>
            )}
          </div>
        </div>
        )}

        {canUserEstatus && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Icon name="key" size={16} />
            Estado y Permisos
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Estado</label>
              <div className="mt-1">
                <Badge variant={user.userEstatus === 'Activo' ? 'success' : 'danger'}>
                  {user.userEstatus || 'Activo'}
                </Badge>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Alcance (Scope)</label>
              <p className="text-sm text-gray-900">
                <Badge variant="info">{user.scope || 'all'}</Badge>
              </p>
            </div>
            {canAcl && (
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Campos Prohibidos</label>
              <p className="text-sm text-gray-900">
                {user.forbidden && user.forbidden.length > 0 
                  ? `${user.forbidden.length} campo(s) prohibido(s)`
                  : 'Ninguno (todos los campos permitidos)'
                }
              </p>
            </div>
            )}
          </div>
        </div>
        )}

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#0196A1] hover:bg-[#017a85] text-white rounded-lg text-sm font-medium transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </Modal>
  );
};
